/**
 * Folder helpers for the two-rail page (D26 variant D).
 *
 * Wave 1 navigated five categories baked into the client. Kathy and Jill now
 * own the folder structure in Drive (D24), so the rail renders whatever the
 * server publishes: `pack.folders` is the list, already slugged, named, counted
 * and ordered by the backend. The client's job is to display it, not to know
 * what folders exist.
 *
 * Colour and icon are the two things the server does not send, and both are
 * derived here for the same reason: Kathy can create a folder at any time, so
 * any table keyed by folder id is a table that goes stale without anyone
 * noticing. The tint is hashed from the slug and the icon is matched on a
 * keyword in it, so a folder invented tomorrow renders correctly on first load,
 * on every teacher's screen, with nobody maintaining anything.
 */

import AssignmentIcon from '@material-ui/icons/Assignment'
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'
import ClassIcon from '@material-ui/icons/Class'
import CodeIcon from '@material-ui/icons/Code'
import ExtensionIcon from '@material-ui/icons/Extension'
import FolderOutlinedIcon from '@material-ui/icons/FolderOutlined'
import MenuBookIcon from '@material-ui/icons/MenuBook'

/** The generated folder, pinned first and never displaceable (D30). */
const SOLUTIONS_SLUG = 'code-solutions'

/**
 * Six tints, deliberately muted: they sit behind file rows all day, so they
 * mark a section rather than compete with it.
 */
const TINTS = ['#E8B84B', '#7C8EB8', '#D8846E', '#A184B0', '#8FBF74', '#5BA396']

/** The generated folder gets the product blue, not a hashed tint. */
const SOLUTIONS_TINT = '#4C8DFF'

/**
 * A stable colour for a folder name.
 *
 * FNV-1a over the slug. Any hash would do; what matters is that it is pure and
 * client-side, so the colour survives a rename-and-resync without a round trip
 * and never depends on ordering.
 */
function tintFor(slug) {
  if (slug === SOLUTIONS_SLUG) {
    return SOLUTIONS_TINT
  }
  let hash = 0x811c9dc5
  for (let i = 0; i < slug.length; i += 1) {
    hash ^= slug.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return TINTS[hash % TINTS.length]
}

/**
 * Icons, matched on keywords rather than on exact folder names.
 *
 * The same constraint that rules out a colour table rules out a name table:
 * Kathy owns the folder names and can add one at any time, so `{'answer-keys':
 * AssignmentTurnedIn}` is a map that breaks the first time someone renames a
 * folder to "Answer Keys 2026". Matching on a word inside the slug survives
 * that, and covers the variants nobody thought to enumerate.
 *
 * Order is deliberate, because the first rule that matches wins. A compound
 * name like "Assessment Answer Keys" hits `answer` before `assess`, which is
 * the reading we want: it is a kind of answer key. Reordering these changes
 * what such a folder looks like, so move them only on purpose.
 */
const ICON_RULES = [
  [/answer|key|solution/, AssignmentTurnedInIcon],
  [/assess|quiz|test|exam|rubric/, AssignmentIcon],
  [/guide|lesson|plan|plannin|curricul/, MenuBookIcon],
  [/extension|remix|challenge|project|activit/, ExtensionIcon],
  [/slide|present|deck|slideshow/, ClassIcon],
]

/**
 * The icon for a folder, or a generic folder when nothing matches.
 *
 * The fallback is the point. A folder Kathy invents tomorrow gets
 * `FolderOutlined` in its own hashed tint and reads correctly but generically -
 * never wrongly, and never as the meaningless dot this replaced. The icon is a
 * scanning aid for the four folders a teacher opens every week; the folder's
 * name is what actually labels it, so an unmatched folder loses nothing it
 * needed.
 *
 * A specific icon for a new folder is a keyword added here, not a per-folder
 * entry - which is why the drive README tells Kathy and Jill to ask rather than
 * leaving them to wonder whether they can.
 */
function iconFor(slug, generated = false) {
  if (generated || slug === SOLUTIONS_SLUG) {
    return CodeIcon
  }
  const matched = ICON_RULES.find(([pattern]) => pattern.test(slug))
  return matched ? matched[1] : FolderOutlinedIcon
}

/** The single letter shown in a section's chip. */
function initialFor(name) {
  const trimmed = (name || '').trim()
  return trimmed ? trimmed[0].toUpperCase() : '?'
}

/**
 * A pack's folders, tolerating a server that has not shipped `folders` yet.
 *
 * The deployed dashboard and the deployed function are versioned separately, so
 * for a window either can be older than the other. When `folders` is absent we
 * synthesise it from the legacy `categories` shape, which keeps the page
 * working against the currently-deployed backend instead of rendering nothing.
 */
function foldersOf(pack, legacyCategories = []) {
  if (!pack) {
    return []
  }
  if (Array.isArray(pack.folders) && pack.folders.length > 0) {
    return pack.folders
  }
  return legacyCategories
    .map((cat) => {
      const entry = pack.categories?.[cat.id]
      return {
        slug: cat.id === 'solutions' ? SOLUTIONS_SLUG : cat.id,
        name: cat.label,
        generated: cat.id === 'solutions',
        count: entry?.count || 0,
        groups: entry?.groups || [],
      }
    })
    .filter(folder => folder.count > 0)
}

/** Total files across every folder except the generated solutions. */
function documentTotal(folders) {
  return folders
    .filter(f => !f.generated)
    .reduce((sum, f) => sum + (f.count || 0), 0)
}

/** How many generated code solutions this pack has. */
function solutionsTotal(folders) {
  const found = folders.find(f => f.generated)
  return found ? found.count || 0 : 0
}

/**
 * Resolve a URL slug to a folder.
 *
 * An unknown slug is not an error: Drive folders get renamed, and a teacher
 * following a bookmark from last term should land on the pack rather than an
 * error page (D26). Returning the first folder is that fallback.
 */
function folderBySlug(folders, slug) {
  if (!slug) {
    return folders[0] || null
  }
  return folders.find(f => f.slug === slug) || folders[0] || null
}

/** Match a pack by its id, case-insensitively, so /pwr and /PWR both work. */
function packBySlug(packs, slug) {
  if (!slug) {
    return packs[0] || null
  }
  const needle = String(slug).toLowerCase()
  return packs.find(p => String(p.id).toLowerCase() === needle) || packs[0] || null
}

export {
  SOLUTIONS_SLUG,
  SOLUTIONS_TINT,
  TINTS,
  documentTotal,
  folderBySlug,
  foldersOf,
  iconFor,
  initialFor,
  packBySlug,
  solutionsTotal,
  tintFor,
}
